import { useSetToast } from "@app/components/Toast";
import { useUpdateObjective } from "@app/screens/Objectives/data";
import { useOptimistic } from "@app/shared/utils/optimisticUpdates";
import { useTheme } from "@app/store/themeStore";
import { Icon } from "@design-system/Icon";
import { TagMultiSelect } from "@design-system/Inputs/TagMultiSelect";
import { Textfield } from "@design-system/Inputs/Textfield";
import { FieldWrapper } from "@design-system/Inputs/Textfield/FieldWrapper";
import { useDropdownItemStyles } from "@design-system/Inputs/UniversalDropdown/styles";
import { Box, type BoxProps } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text } from "@design-system/Typography/Text";
import {
    ResponseStatus,
    type GetIndicatorsFilteredQuery,
    type GetObjectiveQuery,
} from "@generated/client/graphql";
import { useSetAtom } from "jotai";
import { useMemo, useRef, type ChangeEvent, type FC } from "react";
import { useTranslation } from "react-i18next";
import { isIndicatorSelectModalOpenAtom } from "../../ctx";
import { useDeleteObjectiveTargets, useIndicatorsFiltered } from "../../data";
import { RESOURCE_NAME, keys } from "./i18n";

type IndicatorsComboboxProps = {
    editable?: boolean;
    objective: GetObjectiveQuery["objective"];
};

function emptyFn() {
    return;
}

export const IndicatorsCombobox: FC<IndicatorsComboboxProps> = ({
    objective,
    editable = true,
}) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const { setToastError } = useSetToast();

    const theme = useTheme(objective.themeId);
    const subThemes = theme?.subthemes || [];
    const subthemeIds = useMemo(
        () => subThemes.map((subtheme) => subtheme.id) || [],
        [subThemes],
    );

    const setIndicatorSelectModalOpen = useSetAtom(
        isIndicatorSelectModalOpenAtom,
    );

    const inputRef = useRef<HTMLInputElement>(null);

    const { indicators: { indicators2: indicators } = { indicators2: [] } } =
        useIndicatorsFiltered(
            {
                subthemeIds,
            },
            objective.entityId,
        );

    const [selected, setSelected] = useOptimistic<string | undefined>(
        objective.indicatorId ?? undefined,
    );
    const [query, setQuery] = useOptimistic(
        objective.indicator?.name ?? t(keys.combobox_no_indicator),
    );

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
    };

    /**
     * Handle the case when the input is blurred,the query has changed,
     * but the selection hasn't changed
     */
    const handleInputBlur = () => {
        const _searchQuery = selected
            ? indicators?.find((indicator) => indicator.id === selected)?.name
            : t(keys.combobox_no_indicator);
        setQuery(_searchQuery ?? "");
    };

    const handleOpenIndicatorSelectModal = () => {
        setIndicatorSelectModalOpen(true);
    };

    const filteredIndicatorGroupedBySubtheme = useMemo(() => {
        const filteredIndicators = indicators?.filter((indicator) => {
            /**
             * Little hack to show all indicators when the query is the no indicator text,
             * or else we'd have to have a state to track if the no indicator is selected.
             */
            if (query === t(keys.combobox_no_indicator)) {
                return true;
            }
            return indicator.name.toLowerCase().includes(query.toLowerCase());
        });

        const filteredIndicatorsGroupedBySubtheme =
            filteredIndicators?.reduce(
                (acc, indicator) => {
                    const subthemeLabel = `${theme.name}>${indicator.subtheme?.name}`;
                    if (!acc[subthemeLabel]) {
                        acc[subthemeLabel] = [];
                    }
                    acc[subthemeLabel].push(indicator);
                    return acc;
                },
                {} as Record<string, GetIndicatorsFilteredQuery["indicators2"]>,
            ) ?? {};

        const filteredSubthemesWithIndicators = Object.keys(
            filteredIndicatorsGroupedBySubtheme,
        ).map((subtheme) => ({
            id: subtheme,
            name: subtheme,
            indicators: filteredIndicatorsGroupedBySubtheme[subtheme],
        }));

        return filteredSubthemesWithIndicators;
    }, [indicators, query, theme.name]);

    const { mutate: update } = useUpdateObjective(
        objective.id,
        objective.entityId,
    );

    const { mutate: deleteTargets } = useDeleteObjectiveTargets(objective.id);

    const updateObjectiveIndicator = ({
        indicatorId,
        noIndicator,
    }: {
        indicatorId?: string;
        noIndicator?: boolean;
    }) => {
        const indicator = indicators?.find(
            (indicator) => indicator.id === indicatorId,
        );

        if (noIndicator && objective.targets.length > 0) {
            deleteTargets(objective.targets.map((target) => target.id));
        }

        update(
            {
                id: objective.id,
                indicatorId: noIndicator ? null : indicatorId,
                ...(!objective?.name ? { name: indicator?.name } : {}),
            },
            {
                onSettled: (data, error) => {
                    if (
                        error ||
                        data?.updateObjective?.status === ResponseStatus.Error
                    ) {
                        setToastError(
                            error
                                ? t(keys.error_update)
                                : `${data?.updateObjective?.error?.message}`,
                        );
                    }
                },
            },
        );
    };

    const handleSelectChange = (value: string | string[] | null) => {
        const indicatorId = Array.isArray(value) ? value[0] : value;
        if (!indicatorId) {
            return;
        }

        const indicatorName = indicators?.find(
            (indicator) => indicator.id === indicatorId,
        )?.name;
        setQuery(indicatorName ?? "");
        setSelected(indicatorId);
        updateObjectiveIndicator({ indicatorId });
    };

    const handleNoIndicator = () => {
        setSelected(undefined);
        setQuery(t(keys.combobox_no_indicator));
        updateObjectiveIndicator({ noIndicator: true });
    };

    return editable ? (
        <TagMultiSelect
            immediate
            value={selected}
            onChange={handleSelectChange}
        >
            <Box w="full">
                <TagMultiSelect.Trigger className="border-none p-0">
                    <FieldWrapper
                        inputRef={inputRef}
                        placeholderUp={!!query}
                        required
                        placeholder={t(keys.no_indicator_selected_button)}
                    >
                        <FlexRow gap="2" alignItems="center">
                            <TagMultiSelect.Input
                                className="w-full"
                                onBlur={handleInputBlur}
                                onChange={handleSearchChange}
                                ref={inputRef}
                                value={query}
                            />
                            <TagMultiSelect.Button className="translate-y-[-7px]">
                                <Icon
                                    className="text-primary"
                                    name="chevronDown"
                                />
                            </TagMultiSelect.Button>
                        </FlexRow>
                    </FieldWrapper>
                </TagMultiSelect.Trigger>
                <Box className="relative">
                    <TagMultiSelect.Options className="w-full absolute top-0 z-10">
                        <FlexCol
                            pt="2"
                            className="overflow-y-auto max-h-[300px]"
                        >
                            {filteredIndicatorGroupedBySubtheme.map(
                                (subThemes) => (
                                    <FlexCol key={subThemes.id} gap="1">
                                        <Text
                                            color="tertiary"
                                            variant="caption-bold"
                                            ml="2.5"
                                        >
                                            {subThemes.name}
                                        </Text>
                                        {subThemes.indicators?.map(
                                            (indicator) => (
                                                <TagMultiSelect.Option
                                                    key={indicator.id}
                                                    value={indicator.id}
                                                    className="data-[focus]:bg-blue-100 gap-2.5 z-10"
                                                >
                                                    <Icon
                                                        color={theme?.color}
                                                        name="tagEmptyCircle"
                                                    />
                                                    <Ellipsis
                                                        asChild
                                                        hasTooltip
                                                        tooltipSideOffset={10}
                                                    >
                                                        <Text>
                                                            {indicator.name}
                                                        </Text>
                                                    </Ellipsis>
                                                    {indicator.id ===
                                                        selected && (
                                                        <Icon
                                                            name="check"
                                                            size="sm"
                                                        />
                                                    )}
                                                </TagMultiSelect.Option>
                                            ),
                                        )}
                                    </FlexCol>
                                ),
                            )}
                        </FlexCol>
                        {filteredIndicatorGroupedBySubtheme.length > 0 && (
                            <hr className="my-0.5" />
                        )}
                        <FlexCol
                            className="bg-white z-10"
                            gap="1"
                            alignItems="start"
                        >
                            <CtaItem onClick={handleNoIndicator}>
                                {t(keys.combobox_no_indicator)}
                            </CtaItem>
                            <CtaItem
                                className="text-brand"
                                onClick={handleOpenIndicatorSelectModal}
                            >
                                {t(keys.combobox_browse_all_indicators)}
                            </CtaItem>
                        </FlexCol>
                    </TagMultiSelect.Options>
                </Box>
            </Box>
        </TagMultiSelect>
    ) : (
        <Box className="pointer-events-none cursor-not-allowed" w="full">
            <Textfield
                required
                placeholder={t(keys.no_indicator_selected_button)}
                value={
                    objective.indicator?.name ?? t(keys.combobox_no_indicator)
                }
                onChange={emptyFn}
            />
        </Box>
    );
};

const CtaItem: FC<BoxProps & { active?: boolean }> = ({
    children,
    className,
    active,
    ...props
}) => {
    const styles = useDropdownItemStyles({ active }, className);
    return (
        <Box className={styles} {...props}>
            {children}
        </Box>
    );
};
